import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const modernGradient: TemplateRegistration = {
  meta: {
    id: 'modern-gradient',
    name: 'Modern Gradient',
    category: 'Modern',
    description: 'Contemporary gradient-heavy design with glass-morphism effects and trendy aesthetics',
    previewImage: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Inter']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge glass-card">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner glass-card">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section">
        <div class="container">
          <h2 class="section-title gradient-text">About Us</h2>
          <div class="gradient-divider"></div>
          <div class="about-layout">
            <p class="about-text">${safeAbout}</p>
            ${heroImage ? `<div class="about-image"><img src="${escapeAttr(heroImage)}" alt="${safeName}" loading="lazy"></div>` : `<div class="about-image-placeholder"></div>`}
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card glass-card">
            <div class="service-icon-wrap">
              <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            </div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section">
        <div class="container">
          <h2 class="section-title gradient-text">Our Services</h2>
          <div class="gradient-divider"></div>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <h2 class="section-title gradient-text">Gallery</h2>
          <div class="gradient-divider"></div>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-quote">&ldquo;</div>
            <p class="testimonial-body">${tContent}</p>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span class="testimonial-role">${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section testimonials-section">
        <div class="testimonials-ribbon">
          <div class="container">
            <h2 class="section-title gradient-text">What People Say</h2>
            <div class="gradient-divider"></div>
            <div class="testimonials-grid">${cards}</div>
          </div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item glass-card">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <h2 class="section-title gradient-text">FAQ</h2>
          <div class="gradient-divider"></div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row glass-card">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section">
        <div class="container">
          <h2 class="section-title gradient-text">Pricing</h2>
          <div class="gradient-divider"></div>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <h2 class="section-title gradient-text">Hours</h2>
          <div class="gradient-divider"></div>
          <ul class="hours-list glass-card">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section">
        <div class="container">
          <h2 class="section-title gradient-text">Get In Touch</h2>
          <div class="gradient-divider"></div>
          <div class="contact-grid">
            <div class="contact-info glass-card">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps &rarr;</a>` : ''}
            </div>
            <form class="contact-form glass-card" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit" class="btn-gradient">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #0f0f1a; --bg-alt: #16162a;
      --primary: #7c3aed; --secondary: #a855f7; --pink: #ec4899;
      --text: #f8fafc; --text-muted: #94a3b8;
      --font: 'Inter', sans-serif;
      --glass-bg: rgba(255,255,255,0.06); --glass-border: rgba(255,255,255,0.12);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1140px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--secondary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .85; }

    /* GRADIENT UTILITIES */
    .gradient-text {
      background: linear-gradient(135deg, var(--primary), var(--secondary), var(--pink));
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .gradient-divider {
      width: 80px; height: 4px; border-radius: 2px;
      background: linear-gradient(90deg, var(--primary), var(--secondary));
      margin-bottom: 40px;
    }
    .glass-card {
      background: var(--glass-bg);
      backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
      border: 1px solid var(--glass-border);
      border-radius: 16px;
    }
    .btn-gradient {
      background: linear-gradient(135deg, var(--primary), var(--secondary));
      color: #fff; border: none; padding: 16px 40px; border-radius: 12px;
      font-family: var(--font); font-size: 1rem; font-weight: 600;
      cursor: pointer; transition: transform .2s, box-shadow .2s;
    }
    .btn-gradient:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(124,58,237,.4); }

    /* ANIMATED GRADIENT BG */
    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    /* NAV */
    .navbar {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      background: rgba(15,15,26,.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
      border-bottom: 1px solid var(--glass-border);
    }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px; max-width: 1140px; margin: 0 auto; }
    .nav-brand { font-size: 1.3rem; font-weight: 700; color: var(--text); letter-spacing: -.5px; }
    .nav-links { display: flex; gap: 32px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .88rem; font-weight: 500; transition: color .2s; }
    .nav-links a:hover { color: var(--secondary); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 2px; background: var(--secondary); margin: 6px 0; border-radius: 2px; transition: .3s; }

    /* HERO - text-mask with gradient showing through */
    .hero {
      position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center;
      overflow: hidden; text-align: center;
    }
    .hero-gradient-bg {
      position: absolute; inset: -50%; width: 200%; height: 200%;
      background: linear-gradient(135deg, var(--primary), var(--pink), var(--secondary), #6366f1, var(--primary));
      background-size: 400% 400%;
      animation: gradientShift 12s ease infinite;
      opacity: .35;
    }
    .hero-overlay { position: absolute; inset: 0; background: radial-gradient(ellipse at center, transparent 0%, var(--bg) 75%); }
    .hero-content { position: relative; z-index: 2; padding: 140px 24px 100px; max-width: 1000px; }
    .hero h1 {
      font-size: clamp(4rem, 12vw, 8rem); font-weight: 800; line-height: 1;
      margin-bottom: 24px; letter-spacing: -2px;
      background: linear-gradient(135deg, var(--primary), var(--secondary), var(--pink), #6366f1);
      background-size: 200% 200%;
      animation: gradientShift 8s ease infinite;
      -webkit-background-clip: text; background-clip: text;
      -webkit-text-fill-color: transparent; color: transparent;
    }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.2rem); color: var(--text-muted); max-width: 560px; margin: 0 auto 36px; }
    .hero-cta {
      display: inline-block; padding: 18px 48px;
      background: rgba(255,255,255,0.1); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
      border: 1px solid rgba(255,255,255,0.2); border-radius: 14px;
      color: #fff; font-weight: 600; font-size: 1.05rem;
      transition: background .2s, transform .2s, box-shadow .2s;
    }
    .hero-cta:hover { background: rgba(255,255,255,0.18); transform: translateY(-2px); box-shadow: 0 12px 40px rgba(124,58,237,.3); color: #fff; opacity: 1; }

    /* SECTIONS */
    .section { padding: 96px 0; }
    .section-title { font-size: clamp(2rem, 5vw, 2.8rem); font-weight: 800; margin-bottom: 16px; letter-spacing: -0.5px; }

    /* ABOUT - two-col text + image */
    .about-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
    .about-text { font-size: 1.1rem; color: var(--text-muted); line-height: 1.85; }
    .about-image img { width: 100%; height: 320px; object-fit: cover; border-radius: 16px; border: 1px solid var(--glass-border); }
    .about-image-placeholder { width: 100%; height: 320px; background: var(--glass-bg); border-radius: 16px; border: 1px solid var(--glass-border); }

    /* SERVICES */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .service-card { padding: 36px 28px; transition: transform .3s, box-shadow .3s; }
    .service-card:hover { transform: translateY(-6px); box-shadow: 0 16px 48px rgba(124,58,237,.2); }
    .service-icon-wrap { width: 52px; height: 52px; border-radius: 14px; background: linear-gradient(135deg, var(--primary), var(--secondary)); display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
    .service-icon { color: #fff; }
    .icon-char { font-size: 24px; line-height: 1; }
    .service-card h3 { font-size: 1.15rem; font-weight: 700; margin-bottom: 10px; }
    .service-card p { color: var(--text-muted); font-size: .92rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 14px; font-weight: 700; font-size: 1.05rem; color: var(--secondary); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; border-radius: 14px; display: block; transition: transform .3s; }
    .gallery-item:hover img { transform: scale(1.03); }

    /* TESTIMONIALS */
    .testimonials-section { padding: 0; }
    .testimonials-ribbon {
      background: linear-gradient(135deg, rgba(124,58,237,.15), rgba(168,85,247,.1));
      padding: 96px 0;
    }
    .testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
    .testimonial-card { background: var(--glass-bg); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border: 1px solid var(--glass-border); border-radius: 16px; padding: 32px; position: relative; }
    .testimonial-quote { font-size: 4rem; line-height: 1; color: var(--primary); opacity: .5; position: absolute; top: 16px; left: 24px; font-family: Georgia, serif; }
    .testimonial-body { font-size: 1rem; color: var(--text-muted); line-height: 1.7; margin-bottom: 20px; padding-top: 28px; font-style: italic; }
    .testimonial-author strong { display: block; color: var(--text); font-size: .95rem; }
    .testimonial-role { color: var(--text-muted); font-size: .82rem; }
    .testimonial-stars { margin-top: 6px; }
    .star { color: #475569; font-size: 1rem; }
    .star.filled { color: var(--secondary); }

    /* FAQ */
    .faq-list { max-width: 760px; display: flex; flex-direction: column; gap: 16px; }
    .faq-item { padding: 28px; }
    .faq-q { font-size: 1.1rem; font-weight: 700; margin-bottom: 10px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; font-size: .95rem; }

    /* PRICE LIST */
    .price-list { max-width: 760px; display: flex; flex-direction: column; gap: 16px; }
    .price-row { display: flex; justify-content: space-between; align-items: center; padding: 24px 28px; gap: 24px; }
    .price-info h3 { font-size: 1.05rem; font-weight: 700; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .88rem; }
    .price-amount { font-size: 1.4rem; font-weight: 800; color: var(--secondary); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 440px; padding: 28px 32px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid var(--glass-border); color: var(--text-muted); font-size: .93rem; }
    .hours-list li:last-child { border-bottom: none; }

    /* GOOGLE REVIEWS */
    .google-reviews { padding: 20px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 14px; flex-wrap: wrap; padding: 16px 28px; display: inline-flex; }
    .google-reviews .container { text-align: center; }
    .review-badge .star.filled { color: var(--secondary); }
    .review-text { color: var(--text-muted); font-weight: 600; font-size: .92rem; }

    /* PROMO */
    .promo-banner { padding: 20px 0; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; padding: 20px 28px; }
    .promo-badge { background: linear-gradient(135deg, var(--primary), var(--secondary)); color: #fff; padding: 8px 20px; border-radius: 8px; font-weight: 700; font-size: 1rem; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .88rem; }

    /* CONTACT */
    .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: start; }
    .contact-info { padding: 32px; display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 14px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--secondary); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { margin-top: 8px; font-weight: 600; font-size: .88rem; color: var(--secondary); }
    .contact-form { padding: 32px; display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea {
      background: rgba(255,255,255,0.05); border: 1px solid var(--glass-border); border-radius: 10px;
      color: var(--text); padding: 14px 16px; font-family: var(--font); font-size: .93rem; outline: none; transition: border-color .2s;
    }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--secondary); }

    /* FOOTER */
    .footer {
      background: var(--bg-alt); border-top: 1px solid var(--glass-border);
      padding: 48px 0; text-align: center;
    }
    .footer-brand { font-size: 1.2rem; font-weight: 700; margin-bottom: 8px; }
    .footer p { color: var(--text-muted); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(15,15,26,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .services-grid { grid-template-columns: 1fr; }
      .about-layout { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-grid { grid-template-columns: 1fr; }
      .testimonials-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; align-items: flex-start; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 120px 16px 70px; }
      .services-grid { gap: 16px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-gradient-bg"></div>
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <div class="footer-brand gradient-text">${safeName}</div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
